import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { isPortalRole } from '../utils/rolePaths';
import api from '../utils/api';

const PortalContext = createContext(null);

export function PortalProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portalMe, setPortalMe] = useState(null);
  const [activeStudentId, setActiveStudentId] = useState(null);

  const loadPortalMe = useCallback(async () => {
    if (!user || !isPortalRole(user.role_slug)) {
      setPortalMe(null);
      setActiveStudentId(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/portal/me');
      const data = res.data?.data ?? null;
      setPortalMe(data);
      if (user.role_slug === 'student' && data?.student?.id) {
        setActiveStudentId(data.student.id);
      } else if (user.role_slug === 'parent') {
        const children = data?.children ?? [];
        setActiveStudentId((prev) => {
          if (prev && children.some((c) => c.id === prev)) return prev;
          return children[0]?.id ?? null;
        });
      }
    } catch {
      setPortalMe(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPortalMe();
  }, [loadPortalMe]);

  const value = useMemo(
    () => ({
      loading,
      portalMe,
      activeStudentId,
      setActiveStudentId,
      refreshPortalMe: loadPortalMe,
      portalMode: user?.role_slug === 'parent' ? 'parent' : user?.role_slug === 'student' ? 'student' : null,
      student: portalMe?.student ?? null,
      children: portalMe?.children ?? [],
    }),
    [loading, portalMe, activeStudentId, loadPortalMe, user?.role_slug]
  );

  if (!user || !isPortalRole(user.role_slug)) {
    return children;
  }

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal() {
  return useContext(PortalContext);
}
